// The sidebar's view model, minus React: ChannelListViewModel.computeReload ported over the
// worker's ONE-read `SidebarSnapshot`. Pure — the same snapshot and clock always give the same
// rows — so it is tested without a DOM, and so it could move into the worker unchanged if a very
// large account ever made it worth the hop.
import type { SidebarSnapshot } from '../../bridge/protocol';
import {
  displayName,
  draftPreview,
  groupFaceIds,
  isRootPost,
  matches,
  messagePreview,
  previewSource,
  previewThreadRootId,
  sortedChannelIds,
  unreadState,
  userDisplayName,
} from '../../core/channelListDerivation';
import { channelListSection, displayChannel, type ChannelListSection, type DisplayChannel } from '../../core/channelListDisplayModels';
import { CustomStatusCodec } from '../../core/customStatus';
import { SidebarAttention, type Attention } from '../../core/sidebarAttention';
import { TeamBadges } from '../../core/teamBadges';
import { TeamMute } from '../../core/teamMute';
import { EmojiCharacters } from '../../core/emojiCharacters';
import type { ChannelRecord, PostRecord, TeamRecord, UserRecord } from '../../core/records';
import { t } from '../../i18n/i18n';

export interface TeamTile {
  team: TeamRecord;
  /** Already zero for a silenced team: the strip is the part that interrupts (TeamsHeaderStrip). */
  mentionCount: number;
  hasUnread: boolean;
  /** `satori_team_mute` — the tile dims and carries no badge. */
  isMuted: boolean;
}

export interface SidebarModel {
  sections: ChannelListSection[];
  teams: TeamTile[];
  /** Nothing has ever synced: the ONE moment a whole-pane loading state is allowed. */
  firstRun: boolean;
  draftCount: number;
  usersById: Map<string, UserRecord>;
  channelsById: Map<string, ChannelRecord>;
  me: UserRecord | null;
  myPresence: string | null;
  isAdmin: boolean;
  /**
   * What the whole app should BADGE (tab title / favicon): unmuted mentions, else "something
   * unread" — the Mac's Dock badge over every team and conversation, minus silenced teams.
   * `SidebarAttention.badgeLabel(attention)` gives the "N" / "•" / nothing.
   */
  attention: Attention;
  /** Teams the reader silenced. */
  mutedTeamIds: Set<string>;
}

function dmPartnerId(name: string, me: string): string | null {
  const parts = name.split('__');
  if (parts.length !== 2) return null;
  return parts[0] === me ? parts[1] : parts[0];
}

function dmOtherParticipantId(name: string, me: string): string | null {
  const other = dmPartnerId(name, me);
  return other === me ? null : other;
}

export function buildSidebar(snapshot: SidebarSnapshot, teamId: string | null, now: number): SidebarModel {
  const me = snapshot.currentUserId ?? '';
  const usersById = new Map(snapshot.users.map((u) => [u.id, u]));
  const channelsById = new Map(snapshot.channels.map((c) => [c.id, c]));
  const membersById = new Map(snapshot.members.map((m) => [m.channelId, m]));
  const drafts = new Map(snapshot.drafts.filter((d) => d.rootId === '').map((d) => [d.channelId, d.message]));
  const previews = snapshot.previews;
  const youLabel = t('channelList.preview.you');
  const attachmentLabel = t('channelList.preview.attachment');
  const draftLabel = t('channelList.preview.draft');
  const noMessagesLabel = t('channelList.preview.noMessages');

  const previewOf = (post: PostRecord, channel: ChannelRecord): string =>
    messagePreview({
      message: previewSource(post.message, post.props),
      authorName: usersById.has(post.userId) ? userDisplayName(usersById.get(post.userId)!) : null,
      isOwnMessage: post.userId === me,
      // A DM has one other participant: prefixing every preview with their name says nothing.
      showsAuthorPrefix: channel.type !== 'D',
      youLabel,
      attachmentPlaceholder: attachmentLabel,
    });

  const categories = snapshot.categories.filter((c) => c.teamId === teamId).sort((a, b) => a.sortOrder - b.sortOrder);
  const sections: ChannelListSection[] = [];
  for (const category of categories) {
    const channelIds = snapshot.categoryChannels[category.id] ?? [];
    const rowsById = new Map<string, DisplayChannel>();
    const namesById = new Map<string, string>();
    for (const channelId of channelIds) {
      const channel = channelsById.get(channelId);
      if (!channel) continue;
      let inactive: DisplayChannel['inactive'] = null;
      if (channel.deleteAt !== 0) inactive = 'archived';
      else if (channel.type === 'D') {
        const other = dmOtherParticipantId(channel.name, me);
        if (other && (usersById.get(other)?.deleteAt ?? 0) !== 0) inactive = 'deactivatedPartner';
      }
      const participantIds = snapshot.participants[channelId] ?? [];
      const name = displayName(channel, {
        currentUserId: me,
        usersById,
        participantIds,
        unknownUserFallback: channel.type === 'G' ? t('channelList.unknownGroup') : t('channelList.unknownUser'),
      });
      namesById.set(channelId, name);
      const latest = previews[channelId] ?? null;
      const unread = unreadState(channel, membersById.get(channelId) ?? null, {
        latestPostIsOwnRoot: latest ? latest.userId === me && isRootPost(latest) : false,
        categoryMuted: category.muted,
      });
      let partner: UserRecord | null = null;
      let customEmoji: string | null = null;
      let customText = '';
      if (channel.type === 'D') {
        const pid = dmPartnerId(channel.name, me);
        partner = pid ? usersById.get(pid) ?? null : null;
        if (partner) {
          // Expiry-checked HERE, against this moment: the server leaves expired statuses in props.
          const status = CustomStatusCodec.fromPropsJSON(partner.customStatusJSON);
          if (status && CustomStatusCodec.isActive(status, now)) {
            customEmoji = EmojiCharacters.character(status.emoji);
            customText = status.text;
          }
        }
      }
      const draftLine = drafts.has(channelId) ? draftPreview(drafts.get(channelId)!, draftLabel) : null;
      let preview: string | null = null;
      let previewIsThreadReply = false;
      let previewAt = 0;
      let previewPostId = '';
      if (draftLine) preview = draftLine;
      else if (latest) {
        previewIsThreadReply = !isRootPost(latest);
        previewAt = latest.createAt;
        previewPostId = latest.id;
        preview = previewOf(latest, channel);
      } else if (channel.lastPostAt === 0) {
        // Honest previews: the claim only when the channel truly never had a message.
        preview = noMessagesLabel;
      }
      rowsById.set(
        channelId,
        displayChannel({
          id: channelId,
          type: channel.type,
          name,
          isUnread: unread.isUnread,
          mentionCount: unread.mentionCount,
          isMuted: unread.isMuted,
          lastPostAt: channel.lastPostAt,
          dmPartnerName: partner ? userDisplayName(partner) : null,
          dmPartnerUsername: partner?.username ?? null,
          dmPartnerId: partner?.id ?? null,
          dmPartnerLastPictureUpdate: partner?.lastPictureUpdate ?? 0,
          dmPartnerPresence: partner ? snapshot.statuses[partner.id] ?? null : null,
          dmPartnerCustomStatusEmoji: customEmoji,
          dmPartnerCustomStatusText: customText,
          groupFaces:
            channel.type === 'G'
              ? groupFaceIds({ participantIds, currentUserId: me, usersById })
                  .map((id) => usersById.get(id))
                  .filter((u): u is UserRecord => !!u)
                  .map((u) => ({ userId: u.id, displayName: userDisplayName(u), lastPictureUpdate: u.lastPictureUpdate }))
              : [],
          preview,
          previewIsThreadReply,
          previewAt,
          previewPostId,
          previewThreadRootId: previewThreadRootId(latest, draftLine != null),
          unreadThreadReplies: snapshot.threadReplies?.[channelId] ?? 0,
          inactive,
        }),
      );
    }
    // `channelIds` order, never a map's: for "manual" sorting the server's order IS the answer.
    const ordered = sortedChannelIds(
      channelIds.filter((id) => rowsById.has(id)),
      {
        sorting: category.sorting,
        categoryType: category.type,
        displayNameFor: (id) => namesById.get(id) ?? '',
        // The row's OWN latest activity, not just the channel record's.
        lastPostAtFor: (id) => Math.max(channelsById.get(id)?.lastPostAt ?? 0, previews[id]?.createAt ?? 0),
      },
    );
    const rows = ordered.map((id) => rowsById.get(id)!).filter(Boolean);
    if (!rows.length) continue;
    sections.push(
      channelListSection({ id: category.id, displayName: category.displayName, type: category.type, collapsed: category.collapsed, isMuted: category.muted, rows }),
    );
  }

  // Team tiles: the SAME rule as the rows (TeamBadges.compute — both mutes, your own newest root
  // post), and a silenced team keeps its unread in the list but loses its badge on the strip.
  const mutedTeamIds = TeamMute.decode(snapshot.syncState[TeamMute.syncStateKey]);
  const mutedChannelIds = new Set<string>();
  for (const c of snapshot.categories) if (c.muted) for (const id of snapshot.categoryChannels[c.id] ?? []) mutedChannelIds.add(id);
  const ownLatestRootPostChannelIds = new Set<string>();
  for (const [id, p] of Object.entries(previews)) if (p.userId === me && isRootPost(p)) ownLatestRootPostChannelIds.add(id);
  const badges = TeamBadges.compute({ channels: snapshot.channels, membersByChannelId: membersById, ownLatestRootPostChannelIds, mutedChannelIds });
  const teams: TeamTile[] = snapshot.teams.map((team) => {
    const isMuted = TeamMute.isMuted(team.id, mutedTeamIds);
    const badge = isMuted ? undefined : badges.get(team.id);
    return { team, mentionCount: badge?.mentionCount ?? 0, hasUnread: badge?.hasUnread ?? false, isMuted };
  });
  const attention = SidebarAttention.compute({
    channels: snapshot.channels,
    membersByChannelId: membersById,
    latestPostByChannelId: previews,
    currentUserId: me,
    mutedChannelIds,
    mutedTeamIds,
  });

  return {
    sections,
    teams,
    firstRun: !snapshot.syncState.last_full_sync_at && snapshot.channels.length === 0,
    draftCount: snapshot.drafts.filter((d) => d.message.trim()).length,
    usersById,
    channelsById,
    me: usersById.get(me) ?? null,
    myPresence: snapshot.statuses[me] ?? null,
    isAdmin: /(^|\s)system_admin(\s|$)/.test(snapshot.roles),
    attention,
    mutedTeamIds,
  };
}

/**
 * The app-wide badge straight from a snapshot (tab title / favicon) without building the sidebar:
 * the same rule as `SidebarModel.attention` — both channel mutes, silenced teams, your own newest
 * root post, archived channels excluded.
 */
export function snapshotAttention(snapshot: SidebarSnapshot | null): Attention {
  if (!snapshot?.currentUserId) return { mentionCount: 0, hasUnread: false };
  const mutedChannelIds = new Set<string>();
  for (const c of snapshot.categories) if (c.muted) for (const id of snapshot.categoryChannels[c.id] ?? []) mutedChannelIds.add(id);
  return SidebarAttention.compute({
    channels: snapshot.channels,
    membersByChannelId: new Map(snapshot.members.map((m) => [m.channelId, m])),
    latestPostByChannelId: snapshot.previews,
    currentUserId: snapshot.currentUserId,
    mutedChannelIds,
    mutedTeamIds: TeamMute.decode(snapshot.syncState[TeamMute.syncStateKey]),
  });
}

/** applyFilter: the everyday list hides inactive rows; only a SEARCH may surface them. */
export function filterSections(sections: ChannelListSection[], query: string, unreadOnly: boolean): ChannelListSection[] {
  const q = query.trim();
  const out: ChannelListSection[] = [];
  for (const s of sections) {
    let rows = q
      ? s.rows.filter((r) => matches({ query: q, displayName: r.name, dmPartnerName: r.dmPartnerName, username: r.dmPartnerUsername }))
      : s.rows.filter((r) => r.inactive == null);
    if (unreadOnly) rows = rows.filter((r) => (r.isUnread || r.mentionCount > 0) && !r.isMuted);
    if (rows.length) out.push({ ...s, rows });
  }
  return out;
}
